import * as React from 'react';
import { useIsomorphicEffect, usePrevious } from '../../../hooks';
import {
    DateFieldMinuteStep,
    DateFieldTimeView,
    DEFAULT_DIGITAL_CLOCK_SECTION_HEADINGS,
    DigitalClockSectionHeadingMap,
    DigitalClockSectionPadding,
} from '../../../theming';
import { areStringArraysEqual, FixMeLater } from '../../../utils';
import { Styler, StylerProps } from '../../Styler/Styler';
import { DateFieldSection, DateFieldSectionValue, Direction } from '../useDateField/useDateField.types';
import {
    areDateValuesEqual,
    composeDateFromState,
    getInitialState,
    getNextSectionKey,
    getDefaultSectionValue,
    getSectionOptions,
} from '../useDateField/useDateField.utils';
import { useStyles } from './DigitalClock.styles';
import { DigitalClockOption } from './DigitalClockOption/DigitalClockOption';
import { DigitalClockSection } from './DigitalClockSection/DigitalClockSection';
import { useCulture } from '../../../culture';
import { getLocalisedMeridiemOptions } from '../useDateField/getLocalisedMeridiemOptions';

export interface DigitalClockProps extends StylerProps {
    /**
     * Callback fired when the date value is changed.
     */
    onChange?: (date: Date | null) => void;

    /**
     * Callback fired when an option is clicked.
     */
    onOptionClick?: (view: DateFieldTimeView, value: string | number) => void;

    /**
     * The date value. Used when the component is controlled.
     */
    value?: Date | null;

    /**
     * The default date value. Use when the component is not controlled.
     */
    defaultValue?: Date | null;

    /**
     * The date used to generate the new value when both `value` and `defaultValue` are empty.
     */
    referenceDate?: Date;

    /**
     * The views that will be available to edit.
     */
    views?: Array<DateFieldTimeView>;

    /**
     * The step between minute selections.
     * @default 5
     */
    minuteStep?: DateFieldMinuteStep;

    /**
     * The horizontal padding which applies to each section of the clock.
     * @default default
     */
    sectionPadding?: DigitalClockSectionPadding;

    /**
     * If `true`, each section will have a heading label.
     * @default false
     */
    sectionHeadings?: boolean;

    /**
     * Optional overrides for the default section heading values. Only relevant when sectionHeadings={true}.
     * @default false
     */
    sectionHeadingMap?: DigitalClockSectionHeadingMap;
}

export const DigitalClock: React.ForwardRefExoticComponent<DigitalClockProps & React.RefAttributes<HTMLElement>> =
    React.forwardRef(function DigitalClock(
        props: DigitalClockProps,
        ref: React.ForwardedRef<FixMeLater>
    ): React.JSX.Element {
        const {
            value: valueProp,
            defaultValue,
            referenceDate,
            onChange,
            onOptionClick,
            views = ['hours', 'minutes'],
            minuteStep = 5,
            sectionPadding = 'default',
            sectionHeadings = false,
            sectionHeadingMap = DEFAULT_DIGITAL_CLOCK_SECTION_HEADINGS,
            ...other
        } = props;

        const { locale } = useCulture();

        const valueFromProps = valueProp || defaultValue || null;
        const previousValueFromProps = usePrevious(valueFromProps);

        const previousViews = usePrevious(views);

        // An array of objects describing the current state of each field section.
        const [sections, setSections] = React.useState<Array<DateFieldSection>>(() =>
            getInitialState(valueFromProps, views, minuteStep, locale)
        );

        const [activeSectionKey, setActiveSectionKey] = React.useState<DateFieldTimeView | null>(
            sections[0].key as DateFieldTimeView
        );

        const { styles } = useStyles({ sectionPadding });

        const updateSectionValue = React.useCallback((key: DateFieldTimeView, newValue: DateFieldSectionValue) => {
            setSections((current) => {
                return current.map((section) => {
                    if (section.key === key) {
                        return {
                            ...section,
                            value: newValue,
                            valid: newValue !== null,
                        };
                    }

                    // If this section is empty then default it to the first option.
                    if (section.value == null) {
                        const first = getDefaultSectionValue(section);
                        return {
                            ...section,
                            value: first,
                            valid: first !== null,
                        };
                    }

                    return section;
                });
            });
        }, []);

        const handleChangeSection = React.useCallback(
            (dir: Direction) => {
                const nextKey = getNextSectionKey(activeSectionKey, sections, dir);
                if (nextKey) {
                    setActiveSectionKey(nextKey as DateFieldTimeView);
                }
            },
            [activeSectionKey, sections]
        );

        const handleOptionSelected = React.useCallback(
            (key: DateFieldTimeView, value: string | number) => {
                onOptionClick?.(key, value);

                updateSectionValue(key, value);
                if (key !== activeSectionKey) {
                    setActiveSectionKey(key);
                }
            },
            [activeSectionKey, onOptionClick, updateSectionValue]
        );

        // Effect which manages changes to our `sections` state
        useIsomorphicEffect(() => {
            if (!onChange) {
                return;
            }
            const valueFromState = composeDateFromState(sections, valueFromProps || referenceDate);

            if (!areDateValuesEqual(valueFromProps, valueFromState)) {
                onChange(valueFromState);
            }
        }, [sections]);

        // Effect which manages changes to the incoming value
        useIsomorphicEffect(() => {
            // If "valueFromProps" is different to "valueFromState" we need to re-initialise the section state.
            const valueFromState = composeDateFromState(sections, previousValueFromProps || referenceDate);

            if (!areDateValuesEqual(valueFromProps, valueFromState)) {
                // The value has been changed
                setSections(getInitialState(valueFromProps, views, minuteStep, locale));
            }
        }, [valueFromProps]);

        // Effect which manages changes to the "views" prop
        useIsomorphicEffect(() => {
            if (!previousViews) {
                return;
            }
            if (areStringArraysEqual(previousViews, views)) {
                return;
            }
            setSections(getInitialState(valueFromProps, views, minuteStep, locale));
        }, [views]);

        // Effect to update meridiem options on locale change
        useIsomorphicEffect(() => {
            setSections((current) =>
                current.map((section) =>
                    section.key === 'meridiem'
                        ? {
                              ...section,
                              options: getLocalisedMeridiemOptions(locale),
                              // keep the current value if any
                              value: section.value,
                          }
                        : section
                )
            );
        }, [locale]);

        return (
            <Styler ref={ref} css={styles.root} role="group" data-digital-clock={true} {...other}>
                {sections.map((section) => {
                    const options = getSectionOptions(section);
                    const { key, value } = section;
                    const active = activeSectionKey === key;

                    const noValueSet = value == null;

                    return (
                        <DigitalClockSection
                            aria-label={`Select ${key}`}
                            active={active}
                            onChangeSection={handleChangeSection}
                            activeValue={value}
                            heading={sectionHeadings ? sectionHeadingMap[key as DateFieldTimeView] : undefined}
                            key={key}
                        >
                            {options.map((option, index) => {
                                const { value: optionValue, text } = option;
                                const label = `${text} ${key}`;
                                const selected = optionValue === value;

                                return (
                                    <DigitalClockOption
                                        text={text}
                                        aria-label={label}
                                        selected={selected}
                                        onClick={(_event) =>
                                            handleOptionSelected(key as DateFieldTimeView, optionValue)
                                        }
                                        tabIndex={selected || (index === 0 && noValueSet) ? 0 : -1}
                                        key={optionValue.toString()}
                                    />
                                );
                            })}
                        </DigitalClockSection>
                    );
                })}
            </Styler>
        );
    });
